import React, { useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import SubPage, { PageAction, PageActions } from '#/common/components/SubPage';
import useAssetQuery from '../hooks/useAssetQuery';
import { Tab } from '#/common/components/SubPage/SubPageProps';
import AssetForm from '../components/AssetForm';
import If from '#/common/components/If';
import { Findings } from '#/modules/findings';
import WorkOrders from '#/modules/workOrder/components/WorkOrders/WorkOrders';
import WorkOrderDataGridFsmFlat from '#/modules/workOrder/components/WorkOrderDataGrid/WorkOrderDataGridFsmFlat';
import useAssetRelatedCounts from '../hooks/useAssetRelatedCountsQuery';
import getFormTitle from '#/common/utils/getFormTitle';
import { Asset } from '#/graphql/types';
import { states } from '../utils/mappings';
import CapitalNeeds from '#/modules/capital-need/components/CapitalNeeds/CapitalNeeds';
import useAccessControl from '#/common/hooks/useAccessControl';
import useSession from '#/common/hooks/useSession';
import { UserRole } from '#/common/types/userTypes';

export interface AssetParams {
  readonly id?: string;
}
export type AssetTabs =
  | 'details'
  | 'work-orders'
  | 'findings'
  | 'capital-needs'
  | 'fsm-work-orders';

export const defaultTab: AssetTabs = 'details';

export interface AssetsProps {
  readonly tab?: AssetTabs;
}

const tabs: Tab<AssetTabs>[] = [
  {
    key: 'details',
    text: 'Details',
    type: 'form',
  },
  {
    key: 'work-orders',
    text: 'TMM Work Orders',
    type: 'grid',
  },
  {
    key: 'fsm-work-orders',
    text: 'FSM Work Orders',
    type: 'grid',
  },
  {
    key: 'findings',
    text: 'Findings',
    type: 'grid',
  },
  {
    key: 'capital-needs',
    text: 'Capital Needs',
    type: 'grid',
  },
];

const woOmitCols = ['asset.assetTag', 'asset'];
const findingsOmitCols = ['asset.assetTag'];

const AssetPage: React.FC<AssetsProps> = ({ tab }) => {
  const { id } = useParams() as AssetParams;
  const { canCreateCapitalNeed } = useAccessControl();
  const { user } = useSession();
  const isTechnician = user?.role === UserRole.technician;
  // Check if we're creating a new asset
  const isNewAsset = id === 'new';

  const assetQuery = useAssetQuery(id, { skip: isNewAsset });
  const asset = assetQuery.data?.getAsset?.data as Asset;

  // Use a ref to store the initial updatedAt value to prevent form remount
  // on background refetches (cache-and-network).
  const formKeyRef = useRef<string | null>(null);
  if (asset && !formKeyRef.current) {
    formKeyRef.current = asset.updatedAt ?? asset.id ?? null;
  }

  const { data } = useAssetRelatedCounts(id ?? '');
  const tenantId = asset?.tenantId;

  const assetTag = asset?.assetTag;
  const title = getFormTitle(id, assetTag, 'Asset');

  const assetsTabs = useMemo(
    () =>
      tabs
        .filter((t) => !(isTechnician && t.key === 'fsm-work-orders'))
        .map((t) => {
          if (t.key === 'work-orders')
            return {
              ...t,
              count: data?.workOrders ?? 0,
            };
          if (t.key === 'findings')
            return {
              ...t,
              count: data?.findings ?? 0,
            };
          if (t.key === 'capital-needs')
            return {
              ...t,
              count: data?.capitalNeeds ?? 0,
            };
          if (t.key === 'fsm-work-orders')
            return {
              ...t,
              count: data?.fsmWorkOrders ?? 0,
            };
          return t;
        }),
    [data, id, tenantId, isTechnician],
  );

  const assetIdParam = asset?.id ? '?assetId=' + asset.id : '';
  const assetIdTenantParam = asset?.id
    ? '?assetId=' + asset.id + '&tenantId=' + (asset?.tenantId ?? '')
    : '';

  const actions = useMemo<PageAction[]>(() => {
    const entityMap: Partial<Record<AssetTabs, { label: string; path: string }>> = {
      'work-orders': { label: 'Work Order', path: '/work-orders/new' + assetIdParam },
      findings: { label: 'Finding', path: '/findings/new' + assetIdParam },
      'capital-needs': { label: 'Capital Need', path: '/capital-needs/new' + assetIdTenantParam },
    };

    const currentEntity = tab ? entityMap[tab] : undefined;
    const hasCreateAccessForTab =
      tab !== 'capital-needs' || (tab === 'capital-needs' && canCreateCapitalNeed);

    return [
      {
        id: `new-${tab}`,
        alwaysShow: true,
        type: 'button',
        props: {
          visible: asset?.state !== states.retired.key && !!currentEntity && hasCreateAccessForTab,
          type: 'submit',
          icon: 'add',
          label: currentEntity ? `Create New ${currentEntity.label}` : '',
          responsive: true,
          path: currentEntity?.path,
        },
      },
    ];
  }, [asset?.id, asset?.tenantId, tab, asset?.state, canCreateCapitalNeed]);

  return (
    <SubPage
      selectedTab={tab || 'details'}
      tabs={isNewAsset ? undefined : assetsTabs}
      title={title}
      isLoading={assetQuery.loading}
      showGoBackButton
      type={!tab || tab === 'details' ? 'form' : 'list'}
      hideSelectToggle={tab === 'fsm-work-orders'}>
      <If condition={tab === 'details' || tab === undefined}>
        {<AssetForm key={`asset-${id}-${formKeyRef.current}`} title={title} asset={asset} />}
      </If>
      <If condition={tab === 'work-orders' || tab === 'findings' || tab === 'capital-needs'}>
        <PageActions actions={actions} />
      </If>
      <If condition={tab === 'work-orders'}>
        {
          <WorkOrders
            omitColumns={woOmitCols}
            permanentFilters={id ? [['assetId', '=', id]] : undefined}
            showOnlyInternal
          />
        }
      </If>
      <If condition={tab === 'findings'}>
        {
          <Findings
            omitColumns={findingsOmitCols}
            permanentFilters={id ? [['assetId', '=', id]] : undefined}
          />
        }
      </If>
      <If condition={tab === 'capital-needs'}>
        <CapitalNeeds permanentFilters={id ? [['assetId', '=', id]] : undefined} />
      </If>
      <If condition={tab === 'fsm-work-orders'}>
        <WorkOrderDataGridFsmFlat
          permanentFilters={id ? [['assetId', '=', id]] : undefined}
          gridId="work-orders-fsm-flat-asset"
        />
      </If>
    </SubPage>
  );
};

export default React.memo(AssetPage);
